import Phaser from 'phaser';

export class EnvironmentSystem {
  private scene: Phaser.Scene;
  private temperature: number = 37; // Celsius
  private pH: number = 7.4;
  private oxygenLevel: number = 100; // Percentage
  private backgroundParticles: any = null; // Using any to avoid type issues
  
  // Particle management
  private particleUpdateTimer: number = 0;
  private particleUpdateInterval: number = 500; // ms
  
  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.setupEnvironment();
  }
  
  update(delta: number): void {
    // Environment-specific update logic
    // For now, just keep environment stable
    
    // Update particle emitter bounds periodically to improve performance
    this.particleUpdateTimer += delta;
    if (this.particleUpdateTimer >= this.particleUpdateInterval) {
      this.updateParticleBounds();
      this.particleUpdateTimer = 0;
    }
  }
  
  private setupEnvironment(): void {
    // Create background particles if Phaser's particle system is available
    if (this.scene.add.particles) {
      // Create a particle texture if it doesn't exist
      if (!this.scene.textures.exists('particle')) {
        const graphics = this.scene.add.graphics();
        graphics.fillStyle(0xFFFFFF, 1);
        graphics.fillCircle(4, 4, 4);
        graphics.generateTexture('particle', 8, 8);
        graphics.destroy();
      }
      
      // Fix for Phaser version compatibility
      try {
        // Get camera bounds for initial particle area
        const camera = this.scene.cameras.main;
        const visibleRect = new Phaser.Geom.Rectangle(
          camera.scrollX - camera.width/2,
          camera.scrollY - camera.height/2,
          camera.width,
          camera.height
        );
        
        // Expand the rectangle to be larger than the camera view
        const expandedRect = new Phaser.Geom.Rectangle(
          visibleRect.x - 400,
          visibleRect.y - 400,
          visibleRect.width + 800,
          visibleRect.height + 800
        );
        
        // Create particle manager with dynamic bounds
        this.backgroundParticles = this.scene.add.particles(0, 0, 'particle', {
          x: { min: expandedRect.x, max: expandedRect.x + expandedRect.width },
          y: { min: expandedRect.y, max: expandedRect.y + expandedRect.height },
          speed: { min: 10, max: 30 },
          angle: { min: 0, max: 360 },
          scale: { start: 0.2, end: 0 },
          alpha: { start: 0.3, end: 0 },  // Reduced alpha for subtler effect
          lifespan: 3000,
          quantity: 1,  // Reduced quantity
          blendMode: 'ADD',
          emitZone: {
            type: 'random',
            source: new Phaser.Geom.Rectangle(
              expandedRect.x, 
              expandedRect.y, 
              expandedRect.width, 
              expandedRect.height
            )
          } as any
        });
        
        // Try to set depth if possible
        try {
          // Different ways to access emitters depending on Phaser version
          if (this.backgroundParticles.setDepth) {
            this.backgroundParticles.setDepth(1);
          } else if (this.backgroundParticles.emitters && this.backgroundParticles.emitters.list) {
            this.backgroundParticles.emitters.list.forEach((emitter: any) => {
              if (emitter.setDepth) emitter.setDepth(1);
            });
          }
        } catch (e) {
          console.warn('Could not set particle depth:', e);
        }
      } catch (error) {
        console.warn('Particle system initialization failed:', error);
      }
    }
  }
  
  private updateParticleBounds(): void {
    // Update particle emitter bounds to follow camera
    if (this.backgroundParticles && this.backgroundParticles.emitters && this.backgroundParticles.emitters.list) {
      const camera = this.scene.cameras.main;
      const visibleRect = new Phaser.Geom.Rectangle(
        camera.scrollX - camera.width / (2 * camera.zoom),
        camera.scrollY - camera.height / (2 * camera.zoom),
        camera.width / camera.zoom,
        camera.height / camera.zoom
      );
      
      // Expand the rectangle to be larger than the camera view
      // This ensures particles appear as the player moves
      const expandedRect = new Phaser.Geom.Rectangle(
        visibleRect.x - 400,
        visibleRect.y - 400,
        visibleRect.width + 800,
        visibleRect.height + 800
      );
      
      // Update emitter zone for each emitter
      this.backgroundParticles.emitters.list.forEach((emitter: any) => {
        if (emitter.emitZone && emitter.emitZone.source) {
          try {
            // Update the emit zone to match the expanded camera view
            emitter.emitZone.source.setTo(
              expandedRect.x,
              expandedRect.y,
              expandedRect.width,
              expandedRect.height
            );
            
            // Also update the x/y bounds
            if (emitter.config && emitter.config.x) {
              emitter.config.x.min = expandedRect.x;
              emitter.config.x.max = expandedRect.x + expandedRect.width;
            }
            
            if (emitter.config && emitter.config.y) {
              emitter.config.y.min = expandedRect.y;
              emitter.config.y.max = expandedRect.y + expandedRect.height;
            }
          } catch (e) {
            // Some versions of Phaser might not support this method
            console.warn('Could not update emitter zone:', e);
          }
        }
      });
      
      // Remove particles that are too far from the camera view
      // This prevents memory issues with infinite particles
      if (this.backgroundParticles.emitters && this.backgroundParticles.emitters.list) {
        const farRect = new Phaser.Geom.Rectangle(
          visibleRect.x - 1000,
          visibleRect.y - 1000,
          visibleRect.width + 2000,
          visibleRect.height + 2000
        );
        
        this.backgroundParticles.emitters.list.forEach((emitter: any) => {
          if (emitter.alive && emitter.alive.length > 0) {
            for (let i = emitter.alive.length - 1; i >= 0; i--) {
              const particle = emitter.alive[i];
              if (!farRect.contains(particle.x, particle.y)) {
                // Remove particles that are too far away
                particle.life = 0;
              }
            }
          }
        });
      }
    }
  }
  
  getTemperature(): number {
    return this.temperature;
  }
  
  setTemperature(temp: number): void {
    this.temperature = temp;
    // Implement effects of temperature change
  }
  
  getPH(): number {
    return this.pH;
  }
  
  setPH(ph: number): void {
    this.pH = Math.max(0, Math.min(14, ph));
    // Implement effects of pH change
  }
  
  getOxygenLevel(): number {
    return this.oxygenLevel;
  }
  
  setOxygenLevel(level: number): void {
    this.oxygenLevel = Math.max(0, Math.min(100, level));
    // Implement effects of oxygen level change
  }
  
  // Create environmental event (e.g., temperature shock, pH change)
  createEnvironmentalEvent(type: string, intensity: number): void {
    switch (type) {
      case 'heat_shock':
        this.temperature += intensity;
        setTimeout(() => {
          this.temperature = 37; // Return to normal after some time
        }, 5000);
        break;
      case 'acid_shock':
        this.pH -= intensity;
        setTimeout(() => {
          this.pH = 7.4; // Return to normal after some time
        }, 5000);
        break;
      case 'oxygen_depletion':
        this.oxygenLevel -= intensity;
        setTimeout(() => {
          this.oxygenLevel = 100; // Return to normal after some time
        }, 5000);
        break;
    }
  }
} 